import { AlpineData, LivewireData, LivewireMethods } from '@/js/interfaces/Components/alpinejs/Estimate/EstimateLineDocumentExporter';
import { translate } from '@/js/app/support/utils';
import { createAlpineLivewireComponent } from '@nolikein/typed-alpinejs';
import { AlpineWired } from '@nolikein/types-livewire3';
import { notifyErrorAlert, notifySuccessAlert } from '@/js/app/support/alert-and-banner';

type Self = AlpineWired.AlpineComponentSelf<AlpineData, LivewireData, LivewireMethods>;

const errorMessages = {
    uploadError: translate('The file upload had an error. Please try again.'),
    mimeError: translate('The file type is invalid. If you think your file extension should be registered, please contact an administrator'),
};

export default {
    name: 'EstimateLineDocumentExporter',
    component: (
        lineId: number,
        allowedMimes: Array<string> = [],
    ) => createAlpineLivewireComponent<AlpineData, LivewireData, LivewireMethods>({
        isUploading: false,
        hasError: false,
        progress: 0,
        file: null,
        lineIdToAttachDocument: lineId,
        allowedMimes,
        openChoiceMenu: false,

        init() {
            this.$watch('file', (file: File | null) => {
                if (null === file) {
                    return;
                }

                // Check mime content front side before sending file to livewire
                const extensionIndex = file.name.lastIndexOf('.');
                const mime = file.name.substring(extensionIndex + 1);

                if (allowedMimes.includes(mime)) {
                    this.submitFileToLivewire();
                } else {
                    notifyErrorAlert(translate(errorMessages.mimeError));
                    this.file = null;
                }
            });
        },

        submitFileToLivewire(): void {
            if (null === this.file) {
                notifyErrorAlert(translate('Cannot submit no file'));
                return;
            }

            this.hasError = false;
            this.isUploading = true;
            this.progress = 0;

            this.$wire.$upload(
                'file',
                this.file,
                () => this.finishCallback(this),
                () => this.errorCallback(this),
                (event: CustomEvent) => this.progressCallback(event, this),
            );
        },

        finishCallback(self: Self): void {
            self.isUploading = false;
            self.dispatchIsUpdating(self);

            // Tell livewire to register the file
            self.$wire.$call('addDocumentToLine', self.lineIdToAttachDocument)
                .then((data) => {
                    self.dispatchIsNotUpdatingAnymore(self);

                    // Notify the user of success
                    notifySuccessAlert(translate(data.successMessage));

                    // Notify the EstimateLineReader component that a document has been attached
                    self.$dispatch('document-attached', {
                        lineId: self.lineIdToAttachDocument,
                        filename: data.documentName,
                        documentId: data.documentId,
                        messageBag: data.messageBag,
                        permissions: data.permissions,
                    });
                });
        },

        errorCallback(self: Self): void {
            self.isUploading = false;
            self.hasError = true;

            notifyErrorAlert(errorMessages.uploadError);
        },

        progressCallback(event: CustomEvent, self: Self): void {
            self.progress = event.detail.progress;
        },

        dispatchIsUpdating(self: Self): void {
            self.$dispatch('is-updating');
        },

        dispatchIsNotUpdatingAnymore(self: Self): void {
            self.$dispatch('is-not-updating-anymore');
        },
    }),
};
